/**
 * documents.js — the legal set.
 *
 * Seven documents, held as markdown strings rather than files fetched at
 * runtime. Three reasons: they are part of the bundle so they cannot 404, they
 * need no loader and no CSP exception, and a legal page that fails to load is
 * worse than no legal page at all.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THESE ARE DRAFTS WRITTEN BY A NON-LAWYER.
 *
 * They are accurate about what the software actually does, which is the part
 * that usually takes a solicitor the longest and costs the most. They are not
 * a substitute for advice. Have them reviewed before launch, and certainly
 * before taking money or selling anything.
 *
 * When the code changes, these change. Specifically: retention periods live in
 * functions/index.js as `expiresAt` values, the processor list depends on what
 * is in package.json and functions/, and the rights section describes
 * exportMyData and deleteAccount. If any of those move, this file is wrong.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Bump when a document changes materially. Recorded against each user's consent. */
export const LEGAL_VERSION = '2026-10-25';

// The date of the last wording change. LEGAL_VERSION moves only when a change
// is material (it re-prompts every member); a clarification of existing
// practice, like the 3 October retention line, moves this alone.
const UPDATED = '25 October 2026';

/* ── Terms of Service ──────────────────────────────────────────────────────── */

const terms = `
## 1. Who we are

Clincordant ("we", "us") operates clincordant.com, a clinical literature briefing and
professional network for healthcare workers. Contact: support@clincordant.com.
Until October 2026 the service was called Leeflet, at leeflet.org; it is the
same service, and accounts carried over unchanged.

By creating an account you agree to these Terms. If you do not agree, do not
use Clincordant.

## 2. Who may use Clincordant

You must be 18 or over. Clincordant is intended for healthcare professionals,
students in a healthcare discipline, and researchers. It is not a source of
medical advice for the public.

You are responsible for everything done under your account. Do not share your
password. Tell us immediately if you believe someone else has access.

## 3. Clincordant is not clinical guidance

This is the most important term in this document and it is repeated in the
Medical Disclaimer.

Summaries, relevance scores, stance labels and news items on Clincordant are
generated by an automated system from published abstracts and public feeds.
They may be incomplete, out of date, or wrong. They are a reason to read the
source, never a substitute for reading it.

**Nothing on Clincordant is medical advice, and nothing on Clincordant should be used
to make a decision about a patient.** Clinical decisions remain entirely your
professional responsibility, under your own regulator's standards.

## 4. What you must not post

You must not post, message or upload:

- **Anything that identifies a patient.** No names, no dates of birth, no NHS
  or hospital numbers, no photographs, no case details specific enough to
  identify someone. This applies even with consent, and even in a direct
  message. It is the fastest way to have your account removed.
- Medical advice directed at an individual, or anything soliciting it.
- Content that is unlawful, defamatory, harassing, discriminatory, or that
  infringes someone else's rights.
- Material you do not have the right to share, including copyrighted text,
  figures or full-text papers.
- Promotional content for medicines or devices, or anything paid for by a
  third party, without disclosing it.
- Automated or bulk activity, scraping, or attempts to circumvent rate limits.

The Community Guidelines expand on this. Breach may result in content removal,
suspension or permanent closure of your account.

## 5. Your content

You keep ownership of what you post. You grant us a non-exclusive, worldwide,
royalty-free licence to host, store, display and distribute it for the purpose
of operating Clincordant. That licence ends when you delete the content, except
where it has already been shared with another user — see the Privacy Policy on
messages and quoted posts.

You are responsible for what you post, including that you have the right to
post it.

## 6. Groups and their moderators

Anyone may create a group. A group's name and description are visible to
everyone signed in; its posts, and the comments and reactions on them, are
visible only to its members and to our moderators. Its owner can appoint
moderators, hand the group to another member, edit its description and
delete it. The owner and moderators
can remove posts and comments from the group and remove members from it; a
removed member cannot rejoin unless a moderator lets them back in. Moderators
cannot act against the owner or against each other.

Group owners and moderators are members like anyone else. They do not act for
Clincordant, and their decisions within a group are theirs. Everything in a group
remains subject to these Terms, and our own moderators can remove content or
close a group whatever its moderators have decided.

If you delete your account while owning a group, it passes to its
longest-serving moderator. A group with no moderators is deleted, and its
members keep their own posts.

## 7. Professional credentials

Where Clincordant displays a job title, specialty or verification mark, that is
based on what you told us and, for verified accounts, on evidence you supplied.
Verification confirms that we saw plausible evidence. It is not an endorsement,
and it does not confirm current registration, fitness to practise, or
competence in any field.

Do not represent yourself as something you are not. Misrepresenting a clinical
role is grounds for immediate permanent closure and, depending on the
circumstances, a report to your regulator.

## 8. Availability, changes and Clincordant Plus

Clincordant is provided as-is. We do not guarantee it will be available,
uninterrupted, or free of errors. We may change, suspend or withdraw features.

Free accounts have usage limits, currently a fixed number of briefing articles
and literature questions per day. Clincordant Plus lifts those limits. It is
granted by us on request, free of charge, at our discretion, and we may
withdraw it; nothing is ever charged for it. If paid features are ever
offered, they will be described at the point of purchase.

## 9. Ending your use

You may delete your account at any time in Settings. We may suspend or close
an account that breaches these Terms, and will normally tell you why.

## 10. Liability

Nothing here limits liability for death or personal injury caused by
negligence, for fraud, or for anything else that cannot lawfully be limited.

Subject to that: we are not liable for any clinical decision you make; for
losses arising from reliance on automated summaries or scores; for content
posted by other users; or for indirect or consequential loss. Where liability
cannot be excluded, it is limited to the greater of the amount you paid us in
the previous twelve months, or £100.

## 11. Law

These Terms are governed by the law of England and Wales, and the courts of
England and Wales have exclusive jurisdiction. If you are a consumer resident
elsewhere in the UK or the EU, you keep the protection of your local mandatory
consumer law.

## 12. Changes to these Terms

We will post material changes here and, where they significantly affect you,
tell you in the app or by email. If you keep using Clincordant after we have told
you, the new Terms apply to you; if you do not accept them, you can delete your
account.
`;

/* ── Privacy Policy ────────────────────────────────────────────────────────── */

const privacy = `
Clincordant is the data controller for the personal data described here. Contact:
support@clincordant.com. Clincordant was called Leeflet until October 2026;
nothing about how your data is handled changed with the name.

## What we collect, and why

**Account and profile.** Email address, name, username, and optionally your
title, job title, specialties, practice location, pronouns, website, headline,
bio, skills, avatar, banner image, and any experience, education or publications
you choose to list. We need this to run your account and to show you to other
users. Everything after "optionally" is yours to leave blank, and you can remove
any of it at any time in Settings.
*Legal basis: performance of our contract with you.*

**What you write.** Posts and articles, comments, reactions, poll votes, direct
messages and their attachments, groups you create or join, events you create or
say you are attending, and reports you submit.
*Legal basis: contract, and our legitimate interest in operating a safe
platform.*

**How you use the briefing.** Which articles you were shown, which you cleared
or hid, and any "was this relevant?" answers. This is what stops the same paper
appearing twice and makes tomorrow's set better. We also keep your reading
streak, the days you finished your briefing and how many papers you have read,
which only you can see. If you switch on the morning email, each morning we
send the titles of that day's papers to your account's email address; turn it
off at any time in Settings → Notifications.
*Legal basis: legitimate interests — you would reasonably expect a
personalised briefing to remember what it has already shown you.*

**Technical data.** IP address, device and browser information, and error logs,
collected when you use the service. Used for security, abuse prevention and
diagnosing faults.
*Legal basis: legitimate interests in the security and integrity of the
service.*

**Whether a conversation is open.** While you have a direct message thread on
screen, your device records that fact against that conversation, and clears it
when you leave. It is used for one thing: so that a message arriving while you
are already reading does not also send you a notification. The other person is
not shown it — it is not a "last seen" or an online indicator, and it is
deleted as soon as you close the thread. Whether you are typing is separate,
is shown to the other person in that conversation, and is equally temporary.
*Legal basis: legitimate interests — a notification for a message you are
watching arrive is a fault, not a feature.*

**Messages you send us.** If you use the contact form, we keep what you wrote,
the subject, and the email address on your account, so we can reply and so we
have a record of what was asked.
*Legal basis: legitimate interests in answering support requests.*

**Verification.** If you apply for clinician or student verification, the work
email you verify with, or your legal name, regulator, registration number and
the documents you upload. See "Verification" below.
*Legal basis: legitimate interests in preventing impersonation of healthcare
professionals; your consent to the upload itself.*

**Marketing.** Only if you opt in, and never pre-ticked. You can withdraw at
any time in Settings.
*Legal basis: consent.*

**Analytics.** Only if you allow analytics cookies. Which pages are visited —
with names and identifiers removed from the address — and simple events such
as signing up, opening a paper (its study type, not which paper) or asking a
question (whether it was answered from papers or directly, never the
question). Used to understand how Clincordant is used and to improve it. See the
Cookie Policy for exactly what is and is not sent.
*Legal basis: consent (UK GDPR article 6(1)(a) and PECR regulation 6). Withdraw
at any time from "Cookie settings".*

**Advertising measurement.** Only if you allow marketing cookies, only on our
public pages, and once when you create an account: whether you arrived from one
of our adverts and whether you then signed up. Never what you do inside the
app.
*Legal basis: consent.*

**Aggregate research statistics.** Only if you switch it on in Settings →
Privacy. It is off until you do, and we record when you turned it on and which
version of this policy was in force at the time.
*Legal basis: consent.*

**Profile views.** When you open someone's profile, we record that you did and
when — the account that viewed, and a timestamp, nothing else. Members on
Clincordant Plus can see who has viewed their own profile in the last 90 days.
Records are deleted after 90 days. You can stop this entirely with **Browse
profiles privately** in Settings → Privacy; if you do, you also cannot see who
viewed yours. Visits to people you have blocked are never recorded.
*Legal basis: legitimate interest — letting professionals see who is taking an
interest in their work, balanced by a one-tap opt-out.*

**Your plan.** Whether you are on the free plan or Clincordant Plus. Plus is granted
by a moderator on request; there is no payment and we hold no card details.
*Legal basis: performance of our contract with you.*

### The research panel, in detail

We may publish or sell aggregate figures about how clinicians read the
literature — the kind of statement that reads "41% of respiratory trainees
opened a paper on inhaled therapy this month". If you have switched the panel
on, your activity contributes to those counts.

**What can contribute:** which topics and specialties the papers you opened
belong to, whether you opened or dismissed a briefing item, the broad
professional group and region on your profile, and coarse timing. Only accounts
verified as clinicians or students contribute, and only activity from after you
switched the panel on.

**What never contributes, whether or not you switch it on:** your name, your
username, your email address, your posts, your comments, your direct messages,
your searches, the questions you put to Ask, your free-text profile fields, your
verification evidence, and the specific papers you saved.

**What we publish is a count, never a row.** No output identifies you, and no
figure is published for a group small enough that you could be picked out of it
— if a cell would describe fewer than a threshold number of people, it is
suppressed rather than rounded.

**Turning it off** stops your activity contributing from that moment. Figures we
have already published cannot be recalled, and we will not claim otherwise; they
are aggregates that contain no personal data, so there is nothing in them to
withdraw.

**We do not sell anything else.** Not your profile, not a list of users, not
access to you. The panel is aggregate statistics or it is nothing.

### Verification

There are two ways to verify, and each collects as little as it can.

**By work or university email.** We email a six-digit code to the address you
give. Once you enter it, we delete the address and keep only its domain (for
example nhs.net) and a one-way fingerprint of it, which stops one address
verifying two accounts. The fingerprint cannot be turned back into the address,
and it is deleted with your account.

**By documents.** You give your legal name, your regulator and, where you have
one, your registration number, and upload evidence. Only Clincordant's own
moderators can open it — nobody else on Clincordant, and no other user, ever sees
it. We email our moderators that a request has arrived, with your name and
regulator, so it can be reviewed.

When a decision is made, the evidence files, your legal name, registration
number and notes are deleted. If nobody has decided within 30 days, they are
deleted anyway and you can apply again. What we keep is the decision itself — approved or not, the tier
and regulator, who decided and why — for 12 months, or until you delete your
account, so that a decision can be explained if it is ever questioned.

### What we deliberately do not collect

We do not ask for, and you must not provide, health information about yourself
or about anyone else. Your specialty and place of work describe your job, not
your health. If you post patient-identifiable information it is a breach of our
Terms and we will remove it.

We do not use advertising trackers, and we do not build advertising profiles.

## Who we share it with

We use a small number of processors, each under contract and each acting only
on our instructions:

| Processor | Purpose | Where |
|---|---|---|
| Google (Firebase) | Hosting, database, authentication, file storage | Belgium (europe-west1) |
| OpenAI | Briefing summaries, relevance scores, and answering questions in Ask | United States |
| Resend | Sending verification and notification email | United States |
| Cloudflare | DNS and content delivery | Global |
| Google (Analytics, Ads) | Usage analytics and advertising measurement — only with your consent | United States and EU |
| LinkedIn, Meta | Advertising measurement on public pages — only with your consent, and only those we use | United States and EU |

**Transfers outside the UK.** OpenAI and Resend process data in the United
States, under the UK International Data Transfer Addendum to the EU Standard
Contractual Clauses. Google, LinkedIn and Meta transfer analytics and
advertising data to the United States under the UK Extension to the EU–US Data
Privacy Framework or the same Addendum. OpenAI does not train its models on data submitted through
its API.

What is sent to OpenAI:

- **For your briefing:** abstract text, and your profile's role, specialty and
  location.
- **For Ask:** the question you type, and — in the assistant mode — the earlier
  turns of that conversation, so a follow-up makes sense. That means anything
  you paste into it is sent too. Do not paste patient-identifiable information;
  see the Medical Disclaimer.

Your name, email address, username, posts and message contents are never sent
to OpenAI.

**Images on news cards.** The world news section shows thumbnails served
directly from the publisher's own website, not copied to ours. That means your
browser contacts the publisher to fetch the picture, and they can see your IP
address and approximate location, exactly as if you had opened their site. We
send no referrer, so they are not told which page you were on, and no cookie of
ours travels with the request. If you would rather this did not happen, blocking
third-party images in your browser stops it without affecting anything else on
Clincordant.

We do not sell personal data that identifies you. Where we publish or licence
usage statistics, they are aggregated so that no individual can be identified
from them, and small groups are suppressed.

We will disclose data where the law requires it, and may disclose it to protect
someone's safety or to investigate a serious breach of our Terms.

## How long we keep it

- Your account and profile: until you delete your account.
- Posts, articles and comments: until you delete them, or your account.
- Groups you own: when you delete your account, each passes to its
  longest-serving moderator. A group with no moderators is deleted; its
  members' own posts are not destroyed — they leave the feeds and remain
  theirs. Events you are hosting are deleted with your account.
- Groups you joined and events you said you would attend: your membership and
  your attendance are removed with your account; the group and the event carry
  on without you.
- Comments on other people's posts: when you delete your account, each is
  emptied and its name, photo and text removed. An empty placeholder stays only
  where others replied to you, so their replies still make sense.
- Removal from a group: if a group's moderators remove you, a record of it
  stays with the group so that you cannot simply rejoin — until they let you
  back in, the group is deleted, or you delete your account.
- Group moderation log: which moderator removed what, kept while the group
  exists and visible only to its owner and moderators. It holds account
  identifiers, never names or copies of what was removed.
- Notifications: 90 days.
- Assistant conversations in Ask: 24 hours from the last message, then deleted
  automatically. Delete one sooner from the list on the Ask page.
- Profile views: 90 days, then deleted automatically. If you delete your
  account, your visits vanish from everyone's viewer list at once — the list
  only ever shows accounts that still exist — and the underlying records are
  gone within the same 90 days.
- Requests for Clincordant Plus: until a moderator decides them, or you withdraw.
- Briefing history (what you have been shown): 12 months.
- Papers you cleared, and your "was this relevant?" answers: until you delete
  your account — they are what stop a paper you have finished from coming back.
- World news items, and the news headlines we index so you can search them:
  up to 40 days.
- Support messages: 24 months, so we can see whether a problem is recurring.
- Reports you file, and the record of what our moderators decided: kept while
  they are needed to deal with repeated breaches by the same account, and
  reviewed at least once a year. A report quotes the content it was about, so
  removing the content does not remove that quote.
- Whether you have a conversation open: deleted when you close it, and in any
  case treated as expired after a minute.
- Verification evidence, legal name and registration number: deleted as soon as
  a decision is made, and the evidence files in any case within 30 days. The
  decision record: 12 months, or until you delete your account.
- Security and error logs: up to 12 months.
- Analytics data held by Google: 14 months, then deleted by Google.
- Advertising measurement cookies: as set by each service, between 3 months
  and a year (see the Cookie Policy).
- Papers you opened, if you are in the research panel: 12 months.

## Messages, and what deletion cannot undo

Two things survive deleting your account, and you should know about them before
you rely on erasure:

**Direct messages you sent to someone else** remain in that person's inbox,
attributed to a deleted user. We cannot reach into another person's
conversation and remove what they received; doing so would let anyone rewrite
someone else's record of a conversation after the fact.

**Posts that other people quoted** keep the snapshot of your words that was
taken at the moment they quoted you, for the same reason — otherwise their
reply is left responding to nothing.

Both are retained on the basis of our legitimate interest, and the interest of
the other user, in the integrity of their own records. If you have a
particular reason to object, contact us and we will consider it.

## Your rights

You can:

- **See your data** and get a copy in a portable format — Settings → Account →
  Export my data.
- **Correct it** — most fields are editable in Settings.
- **Delete your account** — Settings → Account. This requires re-entering your
  password.
- **Object** to processing based on legitimate interests, and **withdraw
  consent** at any time where consent is the basis.
- **Complain** to the Information Commissioner's Office (ico.org.uk) if you
  think we have got something wrong. We would rather you told us first.

## Security

Access to your data is enforced by server-side security rules, not by the app.
Traffic is encrypted in transit. Verification documents are readable only by
administrators and are deleted after review. Direct messages are private
between participants but are **not** end-to-end encrypted — we could in
principle read them, and we say so rather than implying otherwise.

## Changes

We will post changes here and, where they materially affect you, tell you
before they take effect.
`;

/* ── Cookies ───────────────────────────────────────────────────────────────── */

const cookies = `
## The short version

Some storage keeps Clincordant working and is always on. Analytics and marketing
cookies are **off until you switch them on**, and nothing optional is set or
loaded before you choose. You can change your mind at any time from
"Cookie settings" on the home page, at the foot of this page, or in
Settings → Privacy.

The banner only offers what is actually in use: if a category or a service is
not listed there, it is not running.

## Strictly necessary — always on

| What | Purpose | Kept |
|---|---|---|
| Firebase Auth session | Keeps you signed in between visits | Until you sign out |
| Firestore offline cache | Lets the app work on a poor connection and load faster | Until cleared |
| Theme and accent | Remembers light/dark and your colour choice | Until cleared |
| Unread markers | Remembers which briefing and alerts you have already seen on this device, so the dot beside them goes away | Until you sign out |
| Your cookie choices | Remembers what you chose in the banner | 12 months |
| reCAPTCHA / App Check | Confirms requests come from the real app, not a script | Session |

These need no consent under the Privacy and Electronic Communications
Regulations because the service cannot work without them.

## Analytics — only if you allow it

| Service | Purpose | Cookies | Kept |
|---|---|---|---|
| Google Analytics 4 (Google Ireland Ltd) | Counts visits and which features are used, so we can see what works | \`_ga\`, \`_ga_<id>\` | Up to 13 months |

What Google receives: the address of each page with every name and identifier
replaced by a placeholder (a profile is "/u/:handle", a post "/post/:id"),
and simple events such as "opened a paper" with its study type. It never
receives your name, email address, username, posts, messages, searches,
questions to Ask, or which paper you opened. Google signals and
advertising features stay off unless you also allow marketing.

## Marketing — only if you allow it

Used only on our public pages — the home page, sign-in, help and these legal
pages — and once when an account is created, to measure whether our
advertising works. **Never inside the signed-in app**, where the content is
clinical. Depending on the campaigns we run, one or more of these may be in
use; the banner names the category only when at least one is.

| Service | Cookies | Kept |
|---|---|---|
| LinkedIn Insight Tag (LinkedIn Ireland Unlimited Company) | \`li_fat_id\` on our domain; \`lidc\`, \`bcookie\` on LinkedIn's | 30 days on ours; up to 1 year on LinkedIn's |
| Meta Pixel (Meta Platforms Ireland Ltd) | \`_fbp\` | 3 months |
| Google Ads conversion tracking (Google Ireland Ltd) | \`_gcl_au\` | 3 months |

If your browser sends a Global Privacy Control signal, marketing stays off
whatever you choose.

## Changing your mind

Open "Cookie settings" and switch anything off. Withdrawing reloads the page,
so nothing that was running keeps running, and we delete the cookies these
services set on our own domain. Cookies a service set on its own domain are
cleared from your browser's settings.

## How long your choice lasts

Twelve months, or until we change what we ask about — then the banner asks
again.
`;

/* ── Community Guidelines ──────────────────────────────────────────────────── */

const community = `
Clincordant is a professional space. The standard is roughly: would you say this at
a departmental meeting, with your name on it?

## Patient confidentiality comes first

Never post anything that could identify a patient. Not names, not dates, not
images, not a case detailed enough that a colleague could work out who it was.
This holds in direct messages as much as in public posts, and it holds even if
the patient consented — consent given for one purpose does not extend to a
professional network.

This is the one rule where a first breach can mean immediate removal, because
the harm is done the moment it is posted.

## No advice about individuals

Do not ask for or give management advice about a specific patient, yours or
anyone else's. Discuss the evidence, the guideline, the general case. The
moment it becomes "what should I do about this patient", it belongs in a
clinical conversation with people who have the full picture and the
responsibility.

## Be accurate about who you are

Your job title and specialty are shown alongside what you write, and other
people weigh your words accordingly. Claiming a role you do not hold is
misrepresentation, and in a clinical context it is serious. Verification marks
are not decoration.

## Disagree properly

Challenge the argument, the method, the evidence. Do not attack the person.
There is no downvote on Clincordant on purpose — if you think a post is wrong, say
why, and put your name to it.

Harassment, discrimination, and pile-ons are removed and the accounts
suspended.

## Declare your interests

If you have a financial or professional interest in something you are
promoting — you consult for the manufacturer, you are an author on the paper,
you are paid to speak about it — say so in the post. Undisclosed promotion is
removed.

## Copyright

Link to papers. Do not paste full text, figures or tables you do not have the
right to share.

## Running a group

If you own or moderate a group, keep it within these guidelines. Remove
patient-identifiable information the moment you see it, and report it to us as
well — removing it from your group does not remove it from everywhere it has
been seen. Use removal for breaches, not to silence disagreement you could
answer. Our moderators can act in any group, whatever its own moderators have
decided.

## Reporting

Every post, comment, conversation, group and event has a report option.
Reports go to moderators only; the person you report is not told who reported
them. We aim to look at reports about patient information the same day.

## What happens when you breach these

Depending on severity: the content is removed, or your account is suspended, or
it is closed permanently. When we remove something you posted, we tell you what
was removed and why, and you can reply through Help and support if you think we
got it wrong. For anything involving patient information or impersonation of a
clinician, we may also be obliged to notify a regulator or an employer.
`;

/* ── Medical Disclaimer ────────────────────────────────────────────────────── */

const medical = `
## Clincordant is not medical advice

Clincordant summarises published research and public health announcements. It is a
tool for finding and triaging literature. It is not a clinical decision support
system, it is not a source of medical advice, and it has not been assessed or
approved as a medical device.

## The summaries are machine-generated

Every summary, relevance score, stance label and news item on Clincordant is
produced by a large language model from an abstract or a public feed. That
system:

- can misread a finding, or state it more confidently than the source does;
- only sees the abstract, not the full paper, its figures, or its supplementary
  data;
- has no access to whether a paper has since been corrected, retracted, or
  contradicted;
- is prompted to avoid recommendations and to name limitations, but this
  constrains it — it does not guarantee correctness.

**Read the source before you act on anything.** Every card links to it. If the
summary and the paper disagree, the paper is right.

## The assistant answers from a model, with no source behind it

Ask has a second mode that answers your question directly rather than returning
papers — explanations, comparisons, tables, drafting and editing. It is useful,
and it is a different thing from the literature search beside it. Specifically:

- **Nothing it says is sourced.** There is no paper behind a general answer. It
  is a language model writing from what it absorbed in training, and it can be
  confidently wrong about a dose, a threshold, a guideline or a statistic.
- **It has no access to anything live** — not the internet, not guidelines, not
  your trust's policies, not any patient record.
- **It will not advise on an individual patient**, and is instructed to refuse
  requests for management, dosing or diagnosis for a specific person. That
  instruction constrains it; it does not make the refusal certain. Do not rely
  on it to stop you.
- **It is not a medical device** and has not been assessed as one.

**Do not put patient-identifiable information into it.** Not names, dates of
birth, NHS or hospital numbers, or addresses — the same rule that applies
everywhere else on Clincordant, and for the same reason. What you type is sent to
OpenAI to produce the answer.

Conversations in the assistant are saved to your account for **24 hours** so you
can come back to one and carry on, and are then deleted automatically. You can
delete one yourself at any time from the list on the Ask page. Nobody but you
can read them.

The short retention is deliberate. People paste clinical notes into text boxes
despite being asked not to, and a conversation log that lived indefinitely would
become a growing store of text nobody decided to keep.

## What the scores mean, and do not mean

The Clinical Relevance Score estimates how closely a paper matches the
specialty, role and setting you entered. It is not a measure of study quality,
statistical validity, or truth. A high score on a weak study means the weak
study is about your patients.

The confidence label is a machine estimate in the style of GRADE. It is not a
formal GRADE assessment and should not be cited as one.

The stance tally on the question search counts what the retrieved papers found.
It is not a systematic review, it is not weighted by study size or quality, and
a different search would return a different count.

## Your responsibility

If you are a healthcare professional, clinical decisions remain yours, under
your own regulator's standards and your employer's policies. Clincordant does not
change that and cannot share it.

If you are not a healthcare professional: do not use anything here to make
decisions about your own health or anyone else's. Speak to a doctor. In an
emergency in the UK, call 999, or 111 for urgent advice that is not an
emergency.

## Reporting an error

If a summary misstates a paper, tell us at support@clincordant.com with the link.
We want to know, and it helps us improve the prompts that produced it.
`;

/* ── Copyright ─────────────────────────────────────────────────────────────── */

const copyright = `
## What Clincordant displays

For each paper we show the title, journal, publication date, study type, and a
summary written by our own automated system. We do **not** reproduce abstracts,
full text, figures or tables. Bibliographic details are facts; the summary is
our own work; the link takes you to the publisher, who owns the paper.

For news we show the headline, the source, our own summary, and a link. We do
not reproduce the article, and we do not republish the source's own description
of it.

Source data comes from the NCBI E-utilities under NCBI's usage policy, and from
publicly published RSS feeds.

## If you believe we have infringed your copyright

Email support@clincordant.com with:

1. Identification of the work you say is infringed.
2. The exact URL or item on Clincordant you are complaining about.
3. Your contact details.
4. A statement that you believe in good faith that the use is not authorised by
   the rightsholder, their agent, or the law.
5. A statement that the information in your notice is accurate, and that you
   are the rightsholder or authorised to act for them.

We will remove or disable access to the material promptly where the complaint
is valid, and tell the user who posted it.

## Counter-notice

If your content was removed and you believe that was a mistake, reply to the
notice we send you explaining why. We will consider it and, where appropriate,
restore the content.

## Repeat infringers

Accounts that repeatedly post infringing material are closed.

## User-uploaded images

Images posted by users are their responsibility. By uploading, you confirm you
have the right to share the image and to grant us the licence in our Terms.
`;

/* ── Accessibility ─────────────────────────────────────────────────────────── */

const accessibility = `
## Our aim

Clincordant is built to be usable by as many people as possible, including with a
screen reader, keyboard only, magnification, or a browser in forced-colours
mode. A clinician using assistive technology should be able to read their
briefing exactly as anyone else does.

## Where we are

We have built to the Web Content Accessibility Guidelines 2.1 Level AA and
believe the site meets it, but **this has not been independently audited**, so
we are not claiming formal conformance. We would rather say that plainly than
self-certify.

What we have done deliberately:

- Every colour pair in both light and dark themes meets AA contrast.
- Every control is reachable and operable by keyboard, with a visible focus
  indicator.
- Menus and dialogs trap and restore focus, and close on Escape.
- Touch targets are at least 44×44 CSS pixels.
- \`prefers-reduced-motion\` removes animation.
- Forced-colours mode is supported.
- Images uploaded by users can carry alt text, and cards say when it is missing.
- Live regions announce loading states and errors rather than changing silently.

## Known limitations

- Charts and the stance tally convey information through colour and length. Each
  carries a text alternative, but the visual form is not equivalent for everyone.
- Content written by other users — including alt text — is outside our control.
- We have not tested with every combination of assistive technology and browser.

## Telling us about a problem

Email support@clincordant.com describing what you were trying to do, what happened,
and what you were using. Accessibility reports go to the front of the queue.

If you are not satisfied with our response, in the UK you can contact the
Equality Advisory and Support Service.
`;

/* ── Registry ──────────────────────────────────────────────────────────────── */

export const LEGAL_DOCS = {
  terms: {
    title: 'Terms of Service',
    blurb: 'The agreement between you and Clincordant.',
    body: terms,
  },
  privacy: {
    title: 'Privacy Policy',
    blurb: 'What we collect, who processes it, and your rights.',
    body: privacy,
  },
  cookies: {
    title: 'Cookies',
    blurb: 'What we store on your device. There is no tracking.',
    body: cookies,
  },
  community: {
    title: 'Community Guidelines',
    blurb: 'How to behave here, and what gets you removed.',
    body: community,
  },
  medical: {
    title: 'Medical Disclaimer',
    blurb: 'Why nothing on Clincordant is clinical guidance.',
    body: medical,
  },
  copyright: {
    title: 'Copyright',
    blurb: 'What we display, and how to report infringement.',
    body: copyright,
  },
  accessibility: {
    title: 'Accessibility Statement',
    blurb: 'What we have built for, and what we have not tested.',
    body: accessibility,
  },
};

export const LEGAL_ORDER = [
  'terms', 'privacy', 'cookies', 'community', 'medical', 'copyright', 'accessibility',
];

export { UPDATED as LEGAL_UPDATED };
